<template>
  <div class="mt-2">
    <WarningDisable permission="nmap" />
    <div class="flex flex-col gap-6">
      <div>
        <form class="mb-4 space-y-2" @submit.prevent="handleFilter">
          <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
            <label class="label">
              Từ khóa
              <span class="input w-full">
                <i
                  class="fa-solid fa-magnifying-glass text-base-content/80"
                ></i>
                <input
                  type="text"
                  placeholder="Tìm kiếm IP"
                  name="keyword"
                  v-model="keyword"
                />
              </span>
            </label>
            <label class="label">
              Subnet (/24, phân tách bằng dấu phẩy)
              <span class="input input-sm w-full">
                <input
                  type="text"
                  placeholder="10.0.0.0/24, 10.0.1.0/24"
                  v-model="subnets"
                />
              </span>
            </label>
            <label class="label">
              Cổng (phân tách bằng dấu phẩy)
              <span class="input input-sm w-full">
                <input
                  type="text"
                  placeholder="22/tcp, 3389/tcp"
                  v-model="ports"
                />
              </span>
            </label>
            <label class="label cursor-pointer justify-start gap-2">
              <input
                type="checkbox"
                class="checkbox checkbox-sm checkbox-warning"
                v-model="criticalOnly"
              />
              <span class="label-text">Chỉ hiện asset trọng yếu</span>
            </label>
          </div>
          <div class="flex flex-wrap gap-2">
            <input
              class="btn btn-outline grow"
              type="reset"
              value="&#8635; Đặt lại"
              :disabled="loading"
              @click="resetFilter"
            />
            <button
              class="btn btn-primary btn-outline grow"
              type="submit"
              :disabled="!filter_has_changed || loading"
            >
              &#10003; Áp dụng
            </button>
          </div>
        </form>
      </div>

      <div class="flex grow flex-col overflow-x-auto">
        <NetworkScanTabs v-model="activeTab" />

        <div v-if="activeTab === 'log'" class="min-h-0 flex-1">
          <NmapLogViewer :action-id="action_id" />
        </div>

        <div v-else class="card bg-base-100 shadow">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <h2 class="card-title">
                <i class="fa-solid fa-diagram-project mr-2"></i>
                Topology Mạng
              </h2>
              <div class="flex gap-2">
                <button
                  class="btn btn-sm btn-outline"
                  @click="loadResults"
                  :disabled="loading"
                >
                  <i class="fa-solid fa-refresh mr-1"></i>
                  Làm mới
                </button>
              </div>
            </div>

            <div
              v-if="nodes.length > 0"
              class="stats stats-horizontal mb-4 shadow"
            >
              <div class="stat">
                <div class="stat-title">Tổng IP</div>
                <div class="stat-value">{{ nodes.length }}</div>
              </div>
              <div class="stat">
                <div class="stat-title">Kết nối</div>
                <div class="stat-value">{{ edges.length }}</div>
              </div>
            </div>

            <Loading v-if="loading" />
            <NetworkGraph
              v-else-if="nodes.length > 0"
              :data="nodes"
              :edges="edges"
              @node-click="toggleCritical"
            />
            <div v-else class="alert alert-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>Chưa có kết quả nào được tìm thấy.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import Loading from "@/components/LoadingTemplate.vue";
import NetworkGraph, {
  type NetworkNode,
} from "@/components/ClientDetail/nmap/NetworkGraph.vue";
import { computed, nextTick, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import WarningDisable from "../WarningDisable.vue";
import NmapLogViewer from "./NmapLogViewer.vue";
import NetworkScanTabs from "./NetworkScanTabs.vue";

interface NMapNetworkResult extends NetworkNode {
  id: string;
  doc_id?: string;
  agent_id: string;
  scan_id: string;
  ip: string;
  role?: string;
  os?: string;
  hostname?: string;
  critical?: boolean;
  created: number;
  modified: number;
}

interface NMapNetworkEdge {
  id: string;
  from: string;
  to: string;
  port?: string;
  service?: string;
}

const route = useRoute();
const router = useRouter();
const action_id = route.params.action_id as string;

const loading = ref(false);
const nodes = ref<NMapNetworkResult[]>([]);
const edges = ref<NMapNetworkEdge[]>([]);
// Log tab is lazy: the WS only opens when the admin opens it.
const activeTab = ref<"topo" | "log">("topo");

const init_keyword = computed(() => str_from_query("keyword") || "");
const keyword = ref<string | undefined>(init_keyword.value);
const subnets = ref("");
const ports = ref("");
const criticalOnly = ref(false);
const filter_has_changed = computed(
  () =>
    keyword.value !== init_keyword.value ||
    subnets.value !== "" ||
    ports.value !== "" ||
    criticalOnly.value,
);

const splitList = (value: string): string[] =>
  value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

onMounted(() => {
  loadResults();
});

const loadResults = async () => {
  loading.value = true;
  let data: NMapNetworkResult[] = [];
  try {
    const response = await axios_instance.post(
      "/api/v1/view/nmap/network/pagination",
      {
        keyword: keyword.value,
        action_id: action_id,
        subnets: splitList(subnets.value),
        ports: splitList(ports.value),
        critical_only: criticalOnly.value,
        page: 1,
        // ponytail: server caps at defs.ElasticsearchMaxSize (10000); no UI pagination
        size: 10000,
      },
    );
    data = response.data.data || [];
    nodes.value = data;
    edges.value = response.data.edges || [];
  } catch (error) {
    console.error("Error loading nmap network results:", error);
    toast.error("Không thể tải dữ liệu topology mạng!");
  } finally {
    loading.value = false;
  }
  await nextTick();
};

const handleFilter = () => {
  router.push({
    query: {
      ...route.query,
      keyword: keyword.value,
    },
  });
  loadResults();
};

const resetFilter = () => {
  keyword.value = "";
  subnets.value = "";
  ports.value = "";
  criticalOnly.value = false;
  router.push({ query: { ...route.query } });
  loadResults();
};

const toggleCritical = async (node: NetworkNode) => {
  if (!node.doc_id) {
    toast.info("Node này không có bản ghi để đánh dấu.");
    return;
  }
  try {
    await axios_instance.put(
      `/api/v1/view/nmap/network/${node.doc_id}/critical`,
      { critical: !node.critical },
    );
    await loadResults();
  } catch (error) {
    console.error("Error toggling critical flag:", error);
    toast.error("Không thể cập nhật asset trọng yếu!");
  }
};
</script>
